import React, {
  FC, useEffect, useMemo
} from 'react';
import {
  useTranslation
} from 'react-i18next';

import {
  EmptyResultsMessage,
  Spinner,
  successfulToast,
  TableContent,
  TableFooter,
  usePaginationSettings,
} from 'src/shared/components';
import {
  AdminUpdateOrganizationBody,
  useGetAdminOrganizationQuery,
  useGetAdminOrganizationsQuery,
  usePutAdminOrganizationMutation,
} from 'src/redux/openapi';
import {
  CreateOrganizationModal,
  DeleteOrganizationModal,
} from 'src/features/Modals';
import {
  showWarningFromServer
} from 'src/shared/utils';

import {
  ManagementTableRow
} from '../ManagementTableRow';
import {
  OrganisationActions
} from '../../types';
import {
  useManagementAction
} from '../../hooks';

import {
  getOrganizationCols
} from './utils';

interface OrganisationsContentProps {
  searchQuery: string;
}

export const OrganisationsContent: FC<OrganisationsContentProps> = ({
  searchQuery,
}) => {
  const {
    currentPage, setCurrentPage, pageSize, onPageSizeChange
  } = usePaginationSettings(searchQuery);

  const {
    onAction, currentAction, idToModify, removeAction
  } = useManagementAction<OrganisationActions>();

  const {
    data: organizationsData,
    refetch: refetchOrganizations,
    isLoading,
  } = useGetAdminOrganizationsQuery(
    {
      filters: {
        query: searchQuery,
        status: 'ACCEPTED',
      },
      limit: pageSize,
      page: currentPage,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    total: totalOrganizations, organizations
  } = useMemo(
    () => organizationsData?.data || {
      organizations: [],
      total: 0,
    },
    [organizationsData],
  );

  useEffect(
    () => {
      if (!organizations.length && currentPage !== 1) {
        setCurrentPage((prev) => prev - 1);
      }
    },
    [organizations.length]
  );

  const {
    data: orgToEditData
  } = useGetAdminOrganizationQuery(
    {
      id: idToModify || '',
    },
    {
      skip:
        currentAction !== OrganisationActions.EditOrganisation || !idToModify,
      refetchOnMountOrArgChange: true,
    },
  );

  const organizationToEdit = useMemo(
    () => {
      const organization = orgToEditData?.data.organization;

      return {
        ...organization,
        admins:
        organization?.users.map(({
          role, user
        }) => ({
          role,
          email: user.email,
        })) || [],
      };
    },
    [orgToEditData?.data.organization]
  );

  const [saveEditResult] = usePutAdminOrganizationMutation();

  const onEditEnd = async (
    updatedOrganization?: AdminUpdateOrganizationBody,
  ) => {
    if (!updatedOrganization) {
      removeAction();

      return;
    }

    try {
      const response = await saveEditResult({
        id: idToModify || '',
        adminUpdateOrganizationByIdBodySchema: {
          ...updatedOrganization,
        },
      }).unwrap();

      successfulToast(response.message);
      removeAction();
      refetchOrganizations();
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  const {
    t,
    i18n: {
      language
    },
  } = useTranslation();

  const organizationColumns = useMemo(
    () => getOrganizationCols(
      onAction,
      language
    ),
    [onAction, language],
  );

  const orgLabel = t('organisation.organisations');

  if (isLoading) {
    return <Spinner withLoadingText />;
  }

  if (!organizations.length) {
    return (
      <EmptyResultsMessage
        results={orgLabel.toLowerCase()}
        withSearchQuery={!!searchQuery}
      />
    );
  }

  return (
    <>
      <TableContent
        data={organizations}
        columns={organizationColumns}
        renderRow={(row, index) => (
          <ManagementTableRow
            row={row}
            key={row.original.id}
            index={index}
          />
        )}
      />

      <TableFooter
        totalCount={totalOrganizations}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={onPageSizeChange}
        selectorLabel={orgLabel}
      />

      <DeleteOrganizationModal
        isOpen={currentAction === OrganisationActions.DeleteOrg}
        closeModal={removeAction}
        id={idToModify}
        onSuccessDelete={refetchOrganizations}
      />

      <CreateOrganizationModal
        isOpen={currentAction === OrganisationActions.EditOrganisation}
        onClose={removeAction}
        isEditing
        name={organizationToEdit?.name}
        admins={organizationToEdit.admins}
        orgDomain={organizationToEdit?.domain}
        customCreateMethod={onEditEnd}
        customLoginEnabled={organizationToEdit?.customLoginEnabled}
        orgSubdomain={organizationToEdit?.subdomain}
      />
    </>
  );
};
